import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldCheckIcon, RefreshCwIcon, DatabaseIcon, GaugeIcon, CodeIcon, LayoutIcon, ShoppingCartIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteOnderhoudPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website onderhoud | Altijd up-to-date | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website onderhoud is essentieel voor veiligheid en prestaties. Leer waarom regelmatig onderhoud nodig is en wat het kost. Tips van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website onderhoud | Altijd up-to-date | Noord',
      'og:description': 'Website onderhoud is essentieel voor veiligheid en prestaties. Leer waarom regelmatig onderhoud nodig is en wat het kost.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-onderhoud',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe vaak heeft mijn website onderhoud nodig?',
    a: "Wij voeren wekelijks controles uit op je website. Kritieke beveiligingsupdates worden direct doorgevoerd. Reguliere updates van WordPress, thema's en plugins voeren wij maandelijks uit na een grondige test. Zo blijft je website altijd veilig en up-to-date."
  }, {
    q: 'Wat kost website onderhoud?',
    a: 'De kosten voor website onderhoud hangen af van de complexiteit van je website en het gewenste serviceniveau. Wij bieden transparante onderhoudspakketten aan zodat je vooraf precies weet waar je aan toe bent. Neem contact op voor een vrijblijvende offerte.'
  }, {
    q: 'Is website onderhoud verplicht?',
    a: 'Technisch gezien niet, maar het wordt sterk aangeraden. Zonder regelmatig onderhoud loop je risico op beveiligingslekken, trage laadtijden en compatibiliteitsproblemen. Een goed onderhouden website presteert beter in Google en biedt je bezoekers een betere ervaring.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2644" data-mp="1419nd973wyip">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2645" data-mp="143m63okovcwt">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2646" data-mp="q9urw2w0j4ur">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2647" data-mp="v8ee84eiqinn" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2648" data-mp="gb5lp7dhb8uc" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2649" data-mp="lcbc2yjo2k0p" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2650" data-mp="21pt6em9ymj3d">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2651" data-mp="26ma52aescfup">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2652" data-mp="6k0twjg467a6">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2653" data-mp="3ssa4h9ldc6g0">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2654" data-mp="3orqcekfnjt0q">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2655" data-mp="3juq4e3z8sg6z">/</span>
            <span className="text-accent-light font-medium" data-id="element-2656" data-mp="3f1n6xkrcgmli">
              Website onderhoud
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2657" data-mp="3a3dbra5mdlzw">
            Website onderhoud voor bedrijven
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2658" data-mp="312f84sz3fghu">
            Zorg dat je website veilig en snel blijft. Wij nemen het technisch
            onderhoud volledig uit handen zodat jij je kunt focussen op
            ondernemen.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2659" data-mp="1gjbogh58fx6g">
            <a href="#onderhoud-info" onClick={e => scrollToSection(e, 'onderhoud-info')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2660" data-mp="1glxx6k3v0iq9">
              Bekijk onderhoud
              <ArrowRightIcon className="w-5 h-5" data-id="element-2661" data-mp="wwnh3507bvrm" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom onderhoud belangrijk is */}
      <section id="onderhoud-info" className="py-24 bg-surface-muted" data-id="element-2662" data-mp="16lnl52tk9hco">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2663" data-mp="16nrwg5tshimz">
          <div className="text-center mb-16" data-id="element-2664" data-mp="95rqucbe98mh">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2665" data-mp="e5mk2min6aap">
              Waarom website onderhoud belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2666" data-mp="1hf8rklv0f4fc">
              Een website zonder onderhoud is kwetsbaar. Met onze website
              onderhoud service blijft jouw site veilig, snel en betrouwbaar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2667" data-mp="1m7ifjwckduez">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2668" data-mp="1m9mhi8qwkjcm">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2669" data-mp="yi7lsytmt0ac">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2670" data-mp="tkfkje73k1th" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2671" data-mp="17j5kd1lawflj">
                Beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2672" data-mp="12hy9uurrx2b6">
                Regelmatig onderhoud beschermt je website tegen hackers, malware
                en beveiligingslekken.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2673" data-mp="2e0sl4z5u0z6n">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2674" data-mp="2mdvnbbpnr96u">
                <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-2675" data-mp="2qid5x7ka7gg1" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2676" data-mp="2cikgdfkhr69n">Updates</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2677" data-mp="2hfkf15f0h6qq">
                WordPress, thema's en plugins moeten up-to-date blijven voor
                optimale werking en veiligheid.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2678" data-mp="3a3q1bwlpr3so">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2679" data-mp="2vgmydaemg2xy">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-2680" data-mp="30eg5q6rnv2zt" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2681" data-mp="3o4ai6n82ipjj">Backups</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2682" data-mp="3svir7i5hk8w6">
                Dagelijkse backups zorgen ervoor dat je website altijd hersteld
                kan worden bij onvoorziene problemen.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2683" data-mp="1o2axsm29ms5r">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2684" data-mp="1j3g6evq8fva1">
                <GaugeIcon className="w-6 h-6 text-accent" data-id="element-2685" data-mp="1f24olcdloece" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2686" data-mp="1f01o1yetn2tf">
                Performance
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2687" data-mp="26t0z46c6u6wi">
                Optimalisatie van laadsnelheid en database zorgt voor een snelle
                website en betere vindbaarheid.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat wij onderhouden */}
      <section className="py-24 bg-white" data-id="element-2688" data-mp="rc0mj9g28mni">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2689" data-mp="rd1x7ct5z74p">
          <div className="text-center mb-16" data-id="element-2690" data-mp="mcnyildgd9jc">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2691" data-mp="mbw2j9fy19sj">
              Onderhoud voor elk type website
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2692" data-mp="32zdus7pnnmkh">
              Van kleine lokale ondernemers tot middelgrote bedrijven. Wij
              houden je site in topconditie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2693" data-mp="3brpc942sjzob">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors text-center" data-id="element-2694" data-mp="3bu20u03218ac">
              <div className="w-14 h-14 rounded-xl bg-navy-50 flex items-center justify-center mb-6 mx-auto" data-id="element-2695" data-mp="3lpv8p65twdls">
                <CodeIcon className="w-7 h-7 text-navy-700" data-id="element-2696" data-mp="3qooq39o8kor8" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2697" data-mp="3uqtdhqo3cp5f">
                WordPress websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2698" data-mp="1wbp674ctp8e5">
                Onderhoud van WordPress websites inclusief core updates, plugin
                updates en thema-onderhoud. Wij zijn gespecialiseerd in
                WordPress onderhoud.
              </p>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors text-center" data-id="element-2699" data-mp="1jqtxcq0bidc9">
              <div className="w-14 h-14 rounded-xl bg-navy-50 flex items-center justify-center mb-6 mx-auto" data-id="element-2700" data-mp="1op58d42cmtw2">
                <LayoutIcon className="w-7 h-7 text-navy-700" data-id="element-2701" data-mp="1tq2prefjbjwi" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2702" data-mp="1yp5usyvilq4h">
                Bedrijfswebsites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2703" data-mp="22on09saxyn9n">
                Professioneel onderhoud voor bedrijfswebsites. Van kleine
                ondernemers tot middelgrote bedrijven — wij houden je site in
                topvorm.
              </p>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors text-center" data-id="element-2704" data-mp="2hfn8pxbouh5s">
              <div className="w-14 h-14 rounded-xl bg-navy-50 flex items-center justify-center mb-6 mx-auto" data-id="element-2705" data-mp="2meqghpj1z1hg">
                <ShoppingCartIcon className="w-7 h-7 text-navy-700" data-id="element-2706" data-mp="as6d1mouwgzz" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2707" data-mp="5qpdbui3z0kz">Webshops</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2708" data-mp="rmdcogzjkjv">
                Onderhoud van WooCommerce webshops met extra aandacht voor
                beveiliging, betalingsintegraties en productpagina's.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-2709" data-mp="oiia800at5xz">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2710" data-mp="jqi6rxl0ruwn">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2711" data-mp="esgo3dri9xlp">
            Gerelateerde pagina's:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2712" data-mp="1c7ueclgrqntf">
            <a href="#/wordpress-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2713" data-mp="17gujrmv4ghu3">
              WordPress onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2714" data-mp="36ob253fgfy3o">•</span>
            <a href="#/website-hosting" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2715" data-mp="36pm6b4ctb7vb">
              Website hosting
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2716" data-mp="2iygmke3r3vfq">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2717" data-mp="2j0bbe3i587p3">
              Website beveiliging
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-2718" data-mp="2dzdurvcs7lpp">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2719" data-mp="78xmrree9hob">
          <div className="text-center mb-16" data-id="element-2720" data-mp="76l399szmz95">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2721" data-mp="c80pv6atddtn">
              Veelgestelde vragen over website onderhoud
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2722" data-mp="1rqqagpl1wxj5">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-2723" data-mp="1rolx7h19im67" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2724" data-mp="21o4etqtp9vv3">
                  <span className="font-bold text-navy-900" data-id="element-2725" data-mp="2agerdes2idfi">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2726" data-mp="2pdp7x2jki9i3" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2727" data-mp="2kdayph68ctih" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2728" data-mp="q2o2m5n9ahqd">
                    {faq.a}
                  </div>}
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-2729" data-mp="3i7lookcn9l2u">
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-6 py-3 rounded-button shadow-soft hover:shadow-medium transition-all duration-200 group" data-id="element-2730" data-mp="3n7zzga5mr94k">
              Nog vragen? Neem contact op
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" data-id="element-2731" data-mp="2zinholw8lsqn" />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2732" data-mp="38ca8rypi27ti" />
    </main>;
}